import React, { useMemo, Fragment } from 'react'
import {
  MDBTable,
  MDBTableHead,
  MDBTableBody,
} from 'mdb-react-ui-kit'
import { optionsMonth } from '../general/DatosFacturacionDataTable'

/* ======================= TYPES ======================= */

type MonthIndex = number // 0 - 11

export interface PresupuestoLineaNegocio {
  uid: string
  name: string
  months: Record<MonthIndex, number>
}

interface RealLineaNegocio {
  uid: string
  label: string
  months: number[] // length 12
}

interface ReportMonth {
  month: MonthIndex
  ppto: number
  real: number
  variacion: number
  isNegative: boolean
}

interface ReportLineaNegocio {
  uid: string
  name: string
  months: ReportMonth[]
}

export type RealesLineasNegocioMap = Record<string, RealLineaNegocio>

interface Props {
  lineaNegociosDatosPresupuesto: PresupuestoLineaNegocio[]
  lineaNegociosDatosReal: RealesLineasNegocioMap
}

/* ======================= UTILS ======================= */

const formatMoney = (value: number) =>
  value.toLocaleString('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 2,
  })

/* ======================= BUILD REPORT ======================= */

const buildLineasNegocioReport = (
  presupuesto: PresupuestoLineaNegocio[],
  reales: RealesLineasNegocioMap
): ReportLineaNegocio[] => {
  return presupuesto.map((item) => {
    const realItem = reales[item.uid]

    const months: ReportMonth[] = Array.from({ length: 12 }).map(
      (_, month) => {
        const ppto = Number(item.months[month] ?? 0)
        const real = Number(realItem?.months?.[month] ?? 0)
        const variacion = ppto - real

        return {
          month,
          ppto,
          real,
          variacion,
          isNegative: variacion < 0,
        }
      }
    )

    return {
      uid: item.uid,
      name: item.name,
      months,
    }
  })
}

/* ======================= COMPONENT ======================= */

const LineasNegocioReporte = ({
  lineaNegociosDatosPresupuesto,
  lineaNegociosDatosReal,
}: Props) => {

  const reportData = useMemo(
    () =>
      buildLineasNegocioReport(
        lineaNegociosDatosPresupuesto,
        lineaNegociosDatosReal
      ),
    [lineaNegociosDatosPresupuesto, lineaNegociosDatosReal]
  )

  return (
    <MDBTable bordered responsive small hover>
      {/* ===== HEADER ===== */}
      <MDBTableHead className="table-light">
        <tr>
          <th rowSpan={2} className="align-middle">
            Línea de Negocio
          </th>

          {optionsMonth.map((m) => (
            <th key={m.value} colSpan={3} className="text-center">
              {m.label}
            </th>
          ))}
        </tr>

        <tr>
          {optionsMonth.map((_, idx) => (
            <Fragment key={idx}>
              <th className="text-center">PPTO</th>
              <th className="text-center">REAL</th>
              <th className="text-center">VAR</th>
            </Fragment>
          ))}
        </tr>
      </MDBTableHead>

      {/* ===== BODY ===== */}
      <MDBTableBody>
        {reportData.map((row) => (
          <tr key={row.uid}>
            <td className="fw-bold">{row.name}</td>

            {row.months.map((m) => (
              <Fragment key={m.month}>
                <td className="text-end">
                  {formatMoney(m.ppto)}
                </td>

                <td className="text-end">
                  {formatMoney(m.real)}
                </td>

                <td
                  className={`text-end fw-bold ${
                    m.isNegative
                      ? 'text-danger'
                      : 'text-success'
                  }`}
                >
                  {formatMoney(m.variacion)}
                </td>
              </Fragment>
            ))}
          </tr>
        ))}
      </MDBTableBody>
    </MDBTable>
  )
}

export default LineasNegocioReporte;
